import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';

import { GlobalAccount, GUID, OpportunityStatus, OpportunityView, SuggestedOpportunityView } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { ComboboxOption, FormCombobox } from '@/components/ui/combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { FormInput, Input } from '@/components/ui/input';
import { FormSelect, SelectOption } from '@/components/ui/select';
import { FormTextarea } from '@/components/ui/textarea';
import { getHouseholdName, titleCase } from 'utils';
import { useClientsAndProspects } from 'views/household/household-queries';
import { HouseholdDrawer } from 'views/household/HouseholdDrawer';

const OPPORTUNITY_STATUS_OPTIONS: SelectOption[] = Object.values(OpportunityStatus).map((status) => ({
    value: status,
    label: titleCase(status.replace(/_/g, ' ')),
}));

export const toOpportunityFormValues = (opportunity: OpportunityView): Partial<OpportunityFormType> => ({
    name: opportunity.name || '',
    description: opportunity.description || '',
    value: opportunity.value?.toString() || '',
    prospectOption: {
        value: opportunity.householdGUID,
        label: '',
    },
    statusOption: {
        value: opportunity.status,
        label: titleCase(opportunity.status.replace(/_/g, ' ')),
    },
});

// Prefill for converting one of Claire's suggested opportunities. statusOption must be set: when
// defaultValues is passed the form does not fall back to its own defaults, and the output reads it.
export const toSuggestedOpportunityFormValues = (
    suggestion: SuggestedOpportunityView,
): Partial<OpportunityFormType> => ({
    name: suggestion.name || '',
    description: suggestion.description || '',
    value: suggestion.suggestedValue || '',
    statusOption: OPPORTUNITY_STATUS_OPTIONS[0],
});

interface OpportunityFormProps {
    onSubmit: (data: OpportunityFormOutput) => void;
    onCancel: () => void;
    prospectGUID?: string;
    showProspectInput?: boolean;
    defaultValues?: Partial<OpportunityFormType>;
    submitLabel?: string;
}

export interface OpportunityFormRef {
    formMethods: UseFormReturn<OpportunityFormType>;
}

export type OpportunityFormType = {
    name: string;
    description: string;
    value: string;
    prospectOption?: ComboboxOption;
    statusOption: SelectOption;
};

export type OpportunityFormOutput = {
    name: string;
    description: string;
    value: string;
    prospectGUID: string;
    status: OpportunityStatus;
};

const toFormOutput = (data: OpportunityFormType): OpportunityFormOutput => {
    const prospectGUID = data.prospectOption?.value;

    if (!prospectGUID) {
        throw new Error('A prospect must be selected to create an opportunity');
    }

    return {
        name: data.name,
        description: data.description,
        value: data.value,
        prospectGUID,
        status: data.statusOption.value as OpportunityStatus,
    };
};

export const OpportunityForm = forwardRef<OpportunityFormRef, OpportunityFormProps>(
    (
        {
            onSubmit,
            onCancel,
            prospectGUID,
            showProspectInput = true,
            defaultValues,
            submitLabel = 'Create Opportunity',
        },
        ref,
    ) => {
        const [showCreateProspect, setShowCreateProspect] = useState(false);
        const { data: prospects = [], refetch: refetchProspects } = useClientsAndProspects();

        const prospectOptions: ComboboxOption[] = prospects.map((p: any) => ({
            value: p.globalAccountGUID,
            label: getHouseholdName(p),
        }));

        const defaultProspect = prospectGUID
            ? (prospectOptions.find((p) => p.value === prospectGUID) ?? { value: prospectGUID, label: '' })
            : undefined;

        const form = useForm<OpportunityFormType>({
            defaultValues: defaultValues || {
                name: '',
                description: '',
                value: '',
                prospectOption: defaultProspect,
                statusOption: OPPORTUNITY_STATUS_OPTIONS[0],
            },
        });

        useImperativeHandle(ref, () => ({
            formMethods: form,
        }));

        const handleSubmit = (data: OpportunityFormType) => {
            try {
                const output = toFormOutput(data);
                onSubmit(output);
            } catch (error) {
                if (error instanceof Error) {
                    form.setError('prospectOption', { message: error.message });
                }
            }
        };

        const handleProspectCreated = (newProspectGUID: GUID<GlobalAccount>) => {
            // Refetch prospects and update form when complete
            refetchProspects().then(({ data: updatedProspects }) => {
                const newProspect = updatedProspects?.find((p: any) => p.globalAccountGUID === newProspectGUID);
                if (newProspect) {
                    form.setValue('prospectOption', {
                        value: newProspectGUID,
                        label: getHouseholdName(newProspect),
                    });
                }
            });
            setShowCreateProspect(false);
        };

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
                    {showProspectInput && (
                        <>
                            <FormCombobox
                                formMethods={form}
                                name="prospectOption"
                                label="Prospect / Household*"
                                placeholder="Select prospect / household..."
                                options={prospectOptions}
                                required
                                onCustomAction={() => setShowCreateProspect(true)}
                                customActionLabel="Create New Prospect"
                            />
                            {showCreateProspect && (
                                <HouseholdDrawer
                                    onClose={() => setShowCreateProspect(false)}
                                    onSuccess={handleProspectCreated}
                                    isProspectRoute={true}
                                />
                            )}
                        </>
                    )}

                    <FormInput
                        formMethods={form}
                        name="name"
                        label="Name*"
                        placeholder="Enter opportunity name"
                        required
                    />

                    <FormTextarea
                        formMethods={form}
                        name="description"
                        label="Description"
                        placeholder="Enter description (optional)"
                        rows={4}
                    />

                    <FormField
                        control={form.control}
                        name="value"
                        render={({ field: { onChange, value, ...field } }) => (
                            <FormItem>
                                <FormLabel>Value*</FormLabel>
                                <FormControl>
                                    <NumericFormat
                                        {...field}
                                        value={value}
                                        onValueChange={(v) => onChange(v.value)}
                                        customInput={Input}
                                        thousandSeparator
                                        prefix="$"
                                        placeholder="Enter opportunity value"
                                        valueIsNumericString
                                        allowNegative={false}
                                        required
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormSelect
                        formMethods={form}
                        name="statusOption"
                        label="Status*"
                        options={OPPORTUNITY_STATUS_OPTIONS}
                    />

                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={onCancel}>
                            Cancel
                        </Button>
                        <Button type="submit">{submitLabel}</Button>
                    </div>
                </form>
            </Form>
        );
    },
);

OpportunityForm.displayName = 'OpportunityForm';
